/**
 * Ingredient Tabs Carousel
 *  - Desktop carousels: Swiper with 5 visible cards, smooth pan
 *  - Mobile carousels:  Swiper with 2 cols × 2 rows grid, smooth pan
 *  - Both:              .swiper-pagination pill+dots indicator (styled
 *                       globally in base.css :3810–3842)
 *
 * Mirrors the homepage `tabs-with-img-section` Swiper pattern verbatim,
 * which the user confirmed as the correct visual + interaction reference.
 *
 * Also keeps the legacy prev/next arrow handler for sections that still
 * render the older `.ingredient-nav` markup (snippets/ingredient-tabs-updated).
 * That path is unchanged.
 */

(function() {
  'use strict';

  // Track Swiper instances per `.ingredient-cards-swiper` so we don't
  // double-init on Shopify section reload events.
  const swiperByEl = new WeakMap();

  /**
   * Initialise (or re-initialise) Swiper on every `.ingredient-cards-swiper`
   * in the document. Swiper config matches the homepage reference; per-viewport
   * differences are expressed via the `breakpoints` map.
   */
  function initSwipers() {
    if (typeof window.Swiper !== 'function') return;

    const swiperEls = document.querySelectorAll('.ingredient-cards-swiper');
    swiperEls.forEach((el) => {
      // Don't double-init.
      if (swiperByEl.has(el)) return;

      const instance = new window.Swiper(el, {
        // Mobile default
        slidesPerView: 2,
        spaceBetween: 10,
        grid: { rows: 2, fill: 'row' },
        grabCursor: true,
        // Desktop overrides
        breakpoints: {
          769: {
            slidesPerView: 5,
            spaceBetween: 24,
            grid: { rows: 1 },
          },
        },
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true,
        },
      });

      swiperByEl.set(el, instance);
    });
  }

  /**
   * Legacy prev/next arrow handler for sections that still render
   * `[data-ingredient-nav]` buttons (snippets/ingredient-tabs-updated.liquid).
   * Untouched from the original implementation aside from being co-located
   * here. Operates on the native `.ingredient-tab-content.active` scroll —
   * NOT on the new Swiper structure — so it co-exists harmlessly.
   */
  function initCarouselNav() {
    const containers = document.querySelectorAll('.ingredient-tabs-contents-wrapper');

    containers.forEach((container) => {
      const prevBtn = container.querySelector('[data-ingredient-nav="prev"]');
      const nextBtn = container.querySelector('[data-ingredient-nav="next"]');
      if (!prevBtn || !nextBtn) return;

      function getActiveContent() {
        return container.querySelector('.ingredient-tab-content.active');
      }
      function getScrollAmount() {
        const content = getActiveContent();
        if (!content) return 300;
        const card = content.querySelector('.ingredient-card');
        if (!card) return 300;
        return card.offsetWidth + 24;
      }
      function updateButtonStates() {
        const content = getActiveContent();
        if (!content) return;
        const isAtStart = content.scrollLeft <= 10;
        const isAtEnd = content.scrollLeft >= content.scrollWidth - content.clientWidth - 10;
        prevBtn.disabled = isAtStart;
        nextBtn.disabled = isAtEnd;
      }

      prevBtn.addEventListener('click', () => {
        const content = getActiveContent();
        if (content) content.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      });
      nextBtn.addEventListener('click', () => {
        const content = getActiveContent();
        if (content) content.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      });

      container.addEventListener('scroll', updateButtonStates, { capture: true, passive: true });

      const tabTriggers = container.closest('.ingredient-tabs');
      const triggers = tabTriggers ? tabTriggers.querySelectorAll('[tab-trigger]') : null;
      if (triggers) {
        triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => setTimeout(updateButtonStates, 100));
        });
      }

      setTimeout(updateButtonStates, 100);
    });
  }

  function initAll() {
    initCarouselNav();
    initSwipers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section load (theme editor live-edit).
  document.addEventListener('shopify:section:load', initAll);
})();
